import OptionTooltip from '@deltek/specification-client/core/Feedbacks/OptionTooltip'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Box, makeStyles, Theme, Typography } from '@material-ui/core'
import clsx from 'clsx'
import React, { FC, useCallback, useContext } from 'react'

import colors from '../../listingColors'
import { ProductContext } from '../ListingDetailsPage'

const {
    defaultDark,
    defaultLight,
    defaultPrimary,
    defaultSecondary,
    masterPrimary,
    masterSecondary,
    premiumPrimary,
    premiumSecondary,
} = colors

const useStyles = makeStyles((theme: Theme) => ({
    default: {
        display: 'flex',
        boxSizing: 'border-box',
        fontSize: pxToRem(13),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        borderRadius: pxToRem(20),
        lineHeight: '1.7',
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(2),
        color: defaultDark,
        wordBreak: 'break-word',
        textOverflow: 'ellipsis',
        border: `1px solid ${defaultSecondary}`,
        '&:hover': {
            cursor: (disabled: boolean) => {
                disabled ? 'not-allowed' : 'pointer'
            },
        },
    },
    buttonWidth: {
        maxWidth: 'fit-content',
        minWidth: 'fit-content',
        display: 'inline-grid',
    },
    premiumDialogOption: {
        maxWidth: 'max-content',
    },
    editMs: {
        border: `1px solid ${masterPrimary}`,
        color: defaultDark,
        '&:hover': {
            backgroundColor: defaultSecondary,
        },
    },
    editMsSelected: {
        fontWeight: 500,
        backgroundColor: masterPrimary,
        color: theme.palette.secondary.light,
        '&:hover': {
            color: defaultLight,
            backgroundColor: masterSecondary,
        },
        '&::before': {
            content: "'\\2713'",
            marginRight: '5px',
        },
    },
    checked: {
        '&::before': {
            content: "'\\2713'",
            marginRight: '5px',
        },
    },
    pPlusMs: {
        color: defaultDark,
        backgroundColor: defaultLight,
        '&:hover': {
            cursor: 'unset',
        },
    },
    pPlusMsSelected: {
        borderRadius: theme.spacing(2),
        backgroundColor: defaultPrimary,
        '&:hover': {
            cursor: 'unset',
        },
    },
    pPlusPremium: {
        border: `1px solid ${premiumPrimary}`,
        color: premiumPrimary,
        '&:hover': {
            cursor: (disabled: boolean) => {
                disabled ? 'not-allowed' : 'pointer'
            },
            backgroundColor: (disabled: boolean) => {
                !disabled && defaultSecondary
            },
        },
    },
    pPlusPremiumSelected: {
        color: defaultLight,
        borderColor: premiumPrimary,
        backgroundColor: premiumPrimary,
        '&:hover': {
            cursor: (disabled: boolean) => {
                disabled ? 'not-allowed' : 'pointer'
            },
            backgroundColor: (disabled: boolean) => {
                !disabled && premiumSecondary
            },
        },
    },
    pPlusDialogPills: {
        marginRight: theme.spacing(0.5),
        '&:hover': {
            cursor: 'pointer',
        },
    },
    isDeleteable: {
        paddingRight: pxToRem(28),
    },
    tooltip: {
        '&.MuiTooltip-tooltipPlacementBottom': {
            top: 1,
            '& .MuiTooltip-arrow': {
                '&::after': {
                    left: 24,
                },
            },
        },
    },
}))

interface OptionProps {
    value: string
    optionId: string
    propertyName: string
    subPropertyName: string
    isPremiumPlus?: boolean
    isSelected: boolean
    mode?: string
    tooltipText?: string
    disabled?: boolean
}

const Option: FC<OptionProps> = ({
    value,
    optionId,
    propertyName,
    subPropertyName,
    isPremiumPlus,
    isSelected = false,
    mode,
    tooltipText,
    disabled,
}) => {
    const classes = useStyles(disabled)
    const { handleSelection } = useContext(ProductContext)

    const noop = useCallback(() => {}, [])

    return (
        <Typography
            component="span"
            className={clsx(classes.buttonWidth, {
                [classes.premiumDialogOption]: !!isPremiumPlus,
            })}
        >
            <OptionTooltip
                delay={2000}
                title={tooltipText || ''}
                TransitionProps={{
                    onEntered: (node: HTMLElement) => {
                        setTimeout(() => {
                            node.style.display = 'none'
                        }, 3000)
                    },
                    timeout: 0,
                }}
                tooltipClass={classes.tooltip}
            >
                <Box
                    role="button"
                    className={clsx(
                        {
                            [classes.editMs]: mode === 'edit' && !isPremiumPlus,
                            [classes.editMsSelected]:
                                mode === 'edit' && !isPremiumPlus && isSelected,
                            [classes.checked]: mode === 'edit' && isSelected,
                            [classes.pPlusMs]: !(
                                mode === 'edit' && !isPremiumPlus
                            ),
                            [classes.pPlusPremium]:
                                isPremiumPlus && !isSelected,
                            [classes.pPlusPremiumSelected]:
                                isPremiumPlus && isSelected,
                            [classes.pPlusMsSelected]:
                                mode === 'premPlus' &&
                                !isPremiumPlus &&
                                isSelected,
                            [classes.pPlusDialogPills]:
                                mode === 'premPlus' && isPremiumPlus,
                            [classes.isDeleteable]:
                                mode === 'premPlus' && isPremiumPlus,
                        },
                        classes.default
                    )}
                    onClick={
                        mode === 'edit' && !disabled && !!handleSelection
                            ? handleSelection(
                                  propertyName,
                                  subPropertyName || '',
                                  optionId || ''
                              )
                            : noop
                    }
                    data-testid={`option-${optionId}`}
                >
                    {value || 'NA'}
                </Box>
            </OptionTooltip>
        </Typography>
    )
}
export default Option
